import React from "react";
import { motion } from "framer-motion";
import { Badge } from "../ui/badge";

const baseMotion = {
  hidden: { opacity: 0, scale: 0.94, y: 8 },
  show: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

const lineMotion = {
  hidden: { scaleX: 0, scaleY: 0 },
  show: {
    scaleX: 1,
    scaleY: 1,
    transition: { duration: 0.6, ease: "easeInOut", delay: 0.2 },
  },
};

const branchesMotion = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.16,
      delayChildren: 0.45,
    },
  },
};

const branchMotion = {
  hidden: { opacity: 0, y: 12, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

export const RevitLearningPathSection = ({ learningPath }) => {
  if (!learningPath?.isActive) return null;

  return (
    <section className="px-4 pb-8 pt-4 sm:px-6 lg:px-8" data-testid="formations-learning-path-section">
      <div className="mx-auto max-w-7xl rounded-[2.5rem] border border-[#d9e7d5]/85 bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(248,252,243,0.98)_100%)] px-6 py-10 shadow-[0_28px_76px_-52px_rgba(15,118,110,0.28)] sm:px-10 lg:px-12 lg:py-12">
        <div className="text-center">
          <Badge className="rounded-full border border-[#d8ece4] bg-white/92 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-[#0f766e]" data-testid="formations-learning-path-eyebrow" variant="outline">
            {learningPath.title}
          </Badge>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base" data-testid="formations-learning-path-subtitle">
            {learningPath.subtitle}
          </p>
        </div>

        <motion.div
          className="mt-10"
          initial="hidden"
          viewport={{ once: true, amount: 0.45 }}
          whileInView="show"
        >
          <div className="flex flex-col items-center gap-4 lg:hidden" data-testid="formations-learning-path-mobile">
            <motion.div className="w-full max-w-sm rounded-[1.8rem] border border-slate-900/10 bg-slate-950 px-6 py-5 text-center text-white shadow-[0_24px_60px_-38px_rgba(15,23,42,0.75)]" data-testid="formations-learning-path-base-node-mobile" variants={baseMotion}>
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-[#99f6e4]">Départ</div>
              <div className="bim-display mt-2 text-xl font-semibold">{learningPath.baseNodeLabel}</div>
              <div className="mt-2 text-sm text-slate-300">{learningPath.baseNodeDuration}</div>
            </motion.div>

            <motion.div className="h-10 w-[2px] origin-top bg-gradient-to-b from-[#0f766e] to-[#38bdf8]" data-testid="formations-learning-path-mobile-line" variants={lineMotion} />

            <motion.div className="grid w-full max-w-sm gap-4" data-testid="formations-learning-path-mobile-branches" variants={branchesMotion}>
              {learningPath.branches.map((branch) => (
                <motion.div className="rounded-[1.5rem] border border-[#d8ece4] bg-white px-5 py-4 text-center shadow-[0_22px_50px_-42px_rgba(15,118,110,0.35)]" data-testid={`formations-learning-path-branch-${branch.id}`} key={branch.id} variants={branchMotion}>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]">Spécialisation</div>
                  <div className="mt-2 text-sm font-semibold leading-6 text-slate-900">{branch.label}</div>
                  <div className="mt-2 text-xs text-slate-500">{learningPath.branchDurationLabel}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <div className="hidden items-center justify-center gap-6 lg:flex" data-testid="formations-learning-path-desktop">
            <motion.div className="w-[18rem] rounded-[2rem] border border-slate-900/10 bg-slate-950 px-8 py-6 text-center text-white shadow-[0_26px_70px_-44px_rgba(15,23,42,0.8)]" data-testid="formations-learning-path-base-node-desktop" variants={baseMotion}>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#99f6e4]">Départ</div>
              <div className="bim-display mt-3 text-2xl font-semibold leading-tight">{learningPath.baseNodeLabel}</div>
              <div className="mt-3 text-sm text-slate-300">{learningPath.baseNodeDuration}</div>
            </motion.div>

            <motion.div className="h-[2px] w-24 origin-left bg-gradient-to-r from-[#0f766e] to-[#38bdf8]" data-testid="formations-learning-path-desktop-line" variants={lineMotion} />

            <div className="flex items-center gap-5">
              <motion.div className="h-44 w-[2px] origin-top bg-gradient-to-b from-[#0f766e] via-[#14b8a6] to-[#38bdf8]" data-testid="formations-learning-path-branch-line" variants={lineMotion} />
              <motion.div className="grid gap-4" data-testid="formations-learning-path-desktop-branches" variants={branchesMotion}>
                {learningPath.branches.map((branch) => (
                  <motion.div className="rounded-[1.5rem] border border-[#d8ece4] bg-white px-6 py-4 shadow-[0_24px_60px_-46px_rgba(15,118,110,0.35)]" data-testid={`formations-learning-path-branch-card-${branch.id}`} key={branch.id} variants={branchMotion}>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]">Spécialisation</div>
                    <div className="mt-2 text-base font-semibold text-slate-900">{branch.label}</div>
                    <div className="mt-2 text-sm text-slate-500">{learningPath.branchDurationLabel}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};